import axios from 'axios';
import Qs from 'qs';
import Config from '../config/Config';
import rootStore from '../stores/RootStore';

const axiosInstance = axios.create();

var sessionTimeout = null;

axiosInstance.interceptors.request.use(config => {
	const token = localStorage.getItem('token');
	const prepaidToken = sessionStorage.getItem('prepaidToken');

	if (!!token) {
		// Check if Authorization header is already provided (do not override!)
		if (!config.headers['Authorization']) {
			config.headers = {
				...config.headers,
				'Authorization': token
			};
		}

		// Refresh session timeout timer
		if (sessionTimeout !== null) {
			clearTimeout(sessionTimeout);
		}

		sessionTimeout = setTimeout(() => {
			rootStore.userStore.showSessionTimeoutWarning();
		}, (Config.SESSION_TIMEOUT - (5 * 60) ) * 1000);

		// TODO Maybe add request timeout using cancel tokens
		// https://github.com/axios/axios#cancellation
	}

	if (!!prepaidToken) {
		config.headers = {
			...config.headers,
			'X-OTP-Authorization': prepaidToken
		};
	}

	return config;
});

axiosInstance.interceptors.response.use(response => {
	return response;
}, error => {
	if (error && error.response && error.response.status === 401) {
		if (window.location.pathname !== '/account/signin') {
			window.location = '/account/signin';
		}
	}

	// TODO Handle API down (!error.response && error.request)

	return Promise.reject(error);
});

const requests = {
	noScope: () => {
		axiosInstance.defaults.baseURL = Config.API_ENDPOINT;

		return requests;
	},
	public: () => {
		axiosInstance.defaults.baseURL = Config.PUBLIC_API_ENDPOINT;

		return requests;
	},
	publicFz: () => {
		axiosInstance.defaults.baseURL = Config.PUBLIC_API_ENDPOINT_FZ;

		return requests;
	},
	member: () => {
		axiosInstance.defaults.baseURL = Config.MEMBERS_API_ENDPOINT;

		return requests;
	},
	memberFz: () => {
		axiosInstance.defaults.baseURL = Config.MEMBERS_API_ENDPOINT_FZ;

		return requests;
	},
	uaepass: () => {
		axiosInstance.defaults.baseURL = Config.UAEPASS_API_ENDPOINT_FZ;

		return requests;
	},
	legacy: () => {
		axiosInstance.defaults.baseURL = Config.LEGACY_API_ENDPOINT;

		return requests;
	},
	fazaa: () => {
		axiosInstance.defaults.baseURL = Config.NEW_FAZAA_API_ENDPOINT;

		return requests;
	},
	unifiedPayment: () => {
		axiosInstance.defaults.baseURL = Config.UnifiedPayment_API_ENDPOINT;

		return requests;
	},
	get: (url, params = null, config = {}) => {
		if (params !== null) {
			config.params = params;
			config.paramsSerializer = params => Qs.stringify(params, {
				arrayFormat: 'repeat'
			});
		}

		return axiosInstance.get(url, config);
	},
	post: (url, data, upload = false, onProgress = null, config = {}) => {
		if (upload) {
			config.headers = {
				...config.headers,
				'Content-Type': 'multipart/form-data'
			};

			if (!!onProgress) {
				config.onUploadProgress = onProgress;
			}

			return axiosInstance.post(url, data, config);
		} else {
			return axiosInstance.post(url, data, config);
		}
	},
	put: (url, data, config = {}) =>
		axiosInstance.put(url, data, config)
};

const Common = {
	languages: () =>
		requests.publicFz().get('/languages'),
	i18n: () =>
		requests.publicFz().get('/i18n'),
	i18nList: () =>
		requests.publicFz().get('/i18n/list'),
	i18nTimestamp: () =>
		requests.publicFz().get('/i18n/timestamp')
};

const Home = {
	featured: (position = null) => {
		if (rootStore.userStore.authenticated) {
			if (position) {
				return requests.memberFz().get(`/api/home?latitude=${position.latitude}&longitude=${position.longitude}`);
			} else {
				return requests.memberFz().get('/api/home');
			}
		} else {
			if (position) {
				return requests.publicFz().get(`/home?latitude=${position.latitude}&longitude=${position.longitude}`);
			} else {
				return requests.publicFz().get('/home');
			}
		}
	},
	hamadNews: () =>
		requests.publicFz().get('/hamad-news'),
	hamadNewsClick: (id, locale) =>
		requests.publicFz().post(`/hamad-news/${id}/click`, { platform: 'WEB', locale }),
	offers: (position = null) => {
		if (rootStore.userStore.authenticated) {
			if (position) {
				return requests.memberFz().get(`/api/home/offers?latitude=${position.latitude}&longitude=${position.longitude}`);
			} else {
				return requests.memberFz().get('/api/home/offers');
			}
		} else {
			if (position) {
				return requests.publicFz().get(`/home/offers?latitude=${position.latitude}&longitude=${position.longitude}`);
			} else {
				return requests.publicFz().get('/home/offers');
			}
		}
	}
}

const Categories = {
	all: () =>
		requests.publicFz().get('/categories'),
	featured: () =>
		requests.publicFz().get('/categories/featured')
};

const Services = {
	all: () =>
		requests.publicFz().get('/services'),
	get: slug =>
		requests.publicFz().get(`/services/${slug}`),
	featured: () =>
		requests.publicFz().get('/services/featured')
};

const Offers = {
	all: () =>
		requests.publicFz().get('/offers'),
	get: slug =>
		requests.publicFz().get(`/offers/slug/${slug}`),
	featured: () =>
		requests.publicFz().get('/offers/featured'),
	getDynamic: slug =>
		requests.member().get(`/api/extra-offers/${slug}`)
};

const Vouchers = {
	all: () =>
		requests.publicFz().get('/vouchers'),
	get: (slug, preview = false) =>
		requests.publicFz().get(`/vouchers/slug/${slug}`, preview ? { preview: true } : null),
	featured: () =>
		requests.publicFz().get('/vouchers/featured'),
	packages: () =>
		requests.memberFz().get('/api/voucher-package'),
	package: slug =>
		requests.memberFz().get(`/api/voucher-package/${slug}`),
	limits: id =>
		requests.memberFz().get(`/api/v2/voucher-package/limits/${id}`),
	voucherLimit: id =>
		requests.memberFz().get(`/api/voucher/limits/${id}`)
};

const Pages = {
	get: slug =>
		requests.publicFz().get(`/cms/pages/${slug}`)
};

const Content = {
	get: key =>
		requests.publicFz().get(`/cms/contents/${key}`)
};

const User = {
	signIn: (principal, secret) => {
		const data = new FormData();

		data.append('username', principal);
		data.append('password', secret);

		return requests.memberFz().post('/login', data);
	},
	forgot: (authName, language) =>
		requests.publicFz().get(`/forgot-password/${authName}/${language}`),
	forgotReset: data =>
		requests.publicFz().post('/forgot-password/reset', data),
	signOut: () => {},
	me: () => 
		requests.memberFz().get('/api/self'),
	suggest: data =>
		requests.public().post('/partner-suggestion', data),
	companyCode: code =>
		requests.public().get(`/companies/${code}`),
	countries: () =>
		requests.publicFz().get('/legacy/countries'),
	states: countryId =>
		requests.publicFz().get(`/legacy/states/${countryId}`),
	cities: stateId =>
		requests.publicFz().get(`/legacy/cities/${stateId}`),
	levels: () =>
		requests.public().get('/legacy/levels'),
	subscriptions: () =>
		requests.public().get('/legacy/subscriptions'),
	register: data =>
		requests.public().post('/register', data),
	registerPayment: (orderId, data) =>
		requests.public().post(`/register/payment/${orderId}`, data),
	// v2 //
	registerPaymentV2Setup: (orderId, data) =>
		requests.public().post(`/register/payment/v2/setup/${orderId}`, data),
	registerPaymentV2Process: (orderId, data) =>
		requests.public().post(`/register/payment/v2/process/${orderId}`, data),
	registerPaymentV2Complete: authenticationTransactionId =>
		requests.public().post(`/register/payment/v2/process/${authenticationTransactionId}`),
	// --- //
	renew: (data) =>
		requests.member().post('/api/renew', data),
	upgrade: data =>
		requests.member().post('/api/upgrade', data),
	membershipPayment: (orderId, data) =>
		requests.member().post(`/api/membership/payment/${orderId}`, data),
	// v2 //
	membershipPaymentV2Setup: (orderId, data) =>
		requests.member().post(`/api/membership/payment/v2/setup/${orderId}`, data),
	membershipPaymentV2Process: (orderId, data) =>
		requests.member().post(`/api/membership/payment/v2/process/${orderId}`, data),
	membershipPaymentV2Complete: authenticationTransactionId =>
		requests.member().post(`/api/membership/payment/v2/complete/${authenticationTransactionId}`),
	// --- //
	password: data =>
		requests.memberFz().put('/api/update-password', data),
	update: data =>
		requests.memberFz().put('/api/update', data),
	updateV2: data =>
		requests.memberFz().put('/api/v2/update', data),
	orders: (page = 0, limit = Config.RESULTS_PER_PAGE) =>
		requests.memberFz().get(`/api/orders?limit=${limit}&offset=${page * limit}`),
	ordersArchived: (page = 0, limit = Config.RESULTS_PER_PAGE) =>
		requests.memberFz().get(`/api/orders-history?limit=${limit}&offset=${page * limit}`),
	order: orderId =>
		requests.memberFz().get(`/api/orders/${orderId}`),
	orderPDF: (locale, idOrder, idVoucher) =>
		requests.memberFz().get(`/api/vouchers/pdf/${locale}/${idOrder}/${idVoucher}`, null, {
			responseType: 'blob'
		}),
	emailCheck: email =>
		requests.publicFz().get(`/members/email/${email}`),
	mobileCheck: mobile =>
		requests.publicFz().get(`/members/mobile/${mobile}`),
	emiratesIdCheck: emiratesId => 
		requests.publicFz().get(`/members/emiratesId/${emiratesId}`),
	lastTransaction: () =>
		requests.memberFz().get('/api/payment/lastTransaction'),
	persistFavoriteCategories: data =>
		requests.memberFz().post('/api/categories', data),
	submitInsurance: data => 
		requests.member().post('/api/insurance-form', data),
	submitLeaseToOwn: data => 
		requests.memberFz().post('/api/lease-to-own-form', data),
	locations: () =>
		requests.memberFz().get('/api/shipping/locations'),
	addLocation: data => 
		requests.memberFz().post('/api/shipping/location', data),
	defaultLocation: id =>
		requests.memberFz().put(`/api/shipping/location/${id}`),
	deleteLocation: id =>
		requests.memberFz().delete(`/api/shipping/location/${id}`),
	ssoTokenRH: () =>
		requests.memberFz().get('/api/rh-sso'),
	extraOffersSaved: () =>
		requests.memberFz().get('/api/offers-extra/saved'),
	extraOffersTransactions: (page = 0, limit = Config.RESULTS_PER_PAGE) =>
		requests.memberFz().get(`/api/offer-extra/transactions?limit=${limit}&offset=${page * limit}`),
	initiateUAEPassLinking: redirectUri =>
		requests.member().post('/api/uae-pass/associate', { redirectUri }),
	/**
	 * @deprecated
	 */
	unlinkUAEPASS: () =>
		requests.member().put('/api/uae-pass/unlink'),
	initiateUAEPassLogin: redirectUri => {
		const data = new FormData();

		data.append('uaePassRedirectUri', redirectUri);

		return requests.uaepass().post('/login', data);
	},	
	switchToLinkedMemberContext: id =>
		requests.member().post('/uaepass-select', { memberId: id }),
	unlinkMembership: (number, code) =>
		requests.public().get(`/uae-pass/unlink/${number}/${code}`),
};

const SMS = {
	send: data =>
		requests.publicFz().post('/sms-otp', data),
	validate: (id, code) =>
		requests.publicFz().get(`/sms-otp/${id}/${code}`)
};

const Email = {
	send: data =>
		requests.publicFz().post('/email-otp', data),
	validate: (id, code) =>
		requests.publicFz().get(`/email-otp/${id}/${code}`)
};

const Search = {
	search: data =>
		rootStore.userStore.authenticated ? requests.memberFz().get('/api/search', data) : requests.publicFz().get('/search', data),
	quickSearch: data =>
		rootStore.userStore.authenticated ? requests.memberFz().get('/api/quick-search', data) : requests.publicFz().get('/quick-search', data),
	nearme: data =>
		requests.publicFz().get('/hamad/nearme', data),
	locations: () =>
		requests.publicFz().get('/location/map')
};

const Cart = {
	checkout: data =>
		requests.memberFz().post('/api/checkout', data),
	payment: (orderId, data) =>
		requests.memberFz().post(`/api/payment/process/${orderId}`, data),
	// v2 //
	paymentV2Setup: (orderId, data) => 
		requests.memberFz().post(`/api/payment/v2/setup/${orderId}`, data),
	paymentV2Process: (orderId, data) =>
		requests.memberFz().post(`/api/payment/v2/process/${orderId}`, data),
	paymentV2Complete: authenticationTransactionId =>
		requests.memberFz().post(`/api/payment/v2/complete/${authenticationTransactionId}`),
	paymentV3Complete: (authenticationTransactionId, data) =>
		requests.memberFz().post(`/api/order/v3/complete/${authenticationTransactionId}`, data),
	// --- //
	validate: data =>
		requests.publicFz().post('/cart/validate', data)
};

const UnifiedPayment = {
	signJson: data =>
		requests.unifiedPayment().post('api/sign-json', data),
	validateReturn: data =>
		requests.unifiedPayment().post('api/validate-unified-return', data),
	capture: data =>
		requests.unifiedPayment().post('api/unified/capture', data)
};

const Uploads = {
	doUpload: (file, onProgress = null) => {
		const data = new FormData();

		data.append('file', file);

		return requests.memberFz().post('/api/upload', data, true, onProgress);
	}
};

const Cars = {
	list: (page = 0, limit = Config.RESULTS_PER_PAGE) =>
		requests.public().get(`/cars?limit=${limit}&offset=${page * limit}`),
	get: slug =>
		requests.public().get(`/cars/${slug}`)
};

const Estore = {
	search: data =>
		requests.public().get('/store/products/search', data),
	get: (slug, locale) => 
		requests.public().get(`/store/products/item/${slug}?language=${locale}`),
	categories: () =>
		requests.public().get('/store/categories'),
	subCategories: id => 
		requests.public().get(`/store/subcategories/category/${id}`),
	stock: (id, qty) =>
		requests.public().get(`/store/products/stock/${id}/${qty}`)
};

const PrepaidCard = {
	infoBalance: () =>
		requests.member().get('/api/prepaid/service/card/info-and-balance'),
	info: () =>
		requests.member().get('/api/prepaid/service/card/info'),
	balance: () =>
		requests.member().get('/api/prepaid/service/balances'),
	transactions: data =>
		requests.member().post('/api/prepaid/service/transactions', data),
	transfer: data =>
		requests.member().put('/api/prepaid/service/wallet/transfer', data),
	status: data =>
		requests.member().put('/api/prepaid/service/card/status', data, {
			headers: {
				'Content-Type': 'application/json'
			}
		}),
	OTPRequest: () =>
		requests.member().get('/api/prepaid/otp-request'),
	OTPValidate: code =>
		requests.member().post('/api/prepaid/otp-key', code, false, null, {
			headers: {
				'Content-Type': 'application/json'
			}
		})
};

const Payment = {
	getPaymentRequest: id =>
		requests.public().get(`/payment-request/${id}`),
	makePayment: (id, data) =>
		requests.public().post(`/payment-request/${id}`, data)
};

const UsedCars = {
	list: (page = 0, limit = Config.RESULTS_PER_PAGE) =>
		requests.public().get(`/used-cars?limit=${limit}&offset=${page * limit}`),
	get: slug =>
		requests.public().get(`/used-cars/${slug}`)
};

const Fazaa = {
	cancelMembership: (data, ssoToken) => 
		requests.fazaa().post('/member/cancel-account', data, false, null, {
			headers: {
				'Authorization': ssoToken
			}
		})
};

export default {
	Common,
	Home,
	Categories,
	Services,
	Offers,
	Vouchers,
	Pages,
	Content,
	User,
	SMS,
	Email,
	Search,
	Cart,
	UnifiedPayment,
	Uploads,
	Cars,
	Estore,
	PrepaidCard,
	Payment,
	UsedCars,
	Fazaa
};
